import { AlertTriangle, CalendarX, Clock, CreditCard, Trash2 } from "lucide-react";
import { Link } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import { formatOrgDate } from "@/shared/utils/dates";
import { daysUntilLabel } from "@/shared/utils/labels";
import { formatMoney } from "@/shared/utils/numbers";

export type TenantBannerState =
  /** The base plan's trial is nearly over; nothing has been bought yet (ADR-0015 §4). */
  | { kind: "trial_ending"; endsAt: string }
  /** One add-on's own trial clock, which runs separately from the organisation's. */
  | { kind: "addon_trial_ending"; itemName: string; endsAt: string }
  | { kind: "trial_expired"; endedAt: string }
  /** A renewal invoice exists and is not due yet: money to pay, nothing wrong. */
  | { kind: "invoice_issued"; amountPaise: number; dueAt: string | null; onPay?: (() => void) | undefined }
  /** Past the due date, inside the grace window: still working, counting down. */
  | { kind: "overdue"; amountPaise: number; graceUntil: string | null; onPay?: (() => void) | undefined }
  /**
   * The subscription has been cancelled and its last days are running out (M11 §14). Not an error and not
   * money owed — a countdown to a door the customer closed themselves, with the way to reopen it.
   */
  | {
      kind: "subscription_ending";
      endsAt: string;
      onResume?: (() => void) | undefined;
      resuming?: boolean | undefined;
    }
  | {
      kind: "deleting";
      scheduledAt: string;
      /** settings:manage: the cancel action is offered here; everyone else reads where to find it. */
      canCancel: boolean;
      onCancel?: (() => void) | undefined;
      cancelling?: boolean | undefined;
    };

interface TenantStateBannerProps {
  state: TenantBannerState;
  tz: string;
  /** Whether to offer the action: paying and subscribing are `billing:manage`, Owner-only (ADR-0006). */
  canManage?: boolean | undefined;
  /** Only the countdowns read a clock; the kitchen sink pins it so the fixture cannot drift. */
  now?: Date | undefined;
}

const ICON = {
  trial_ending: Clock,
  addon_trial_ending: Clock,
  trial_expired: AlertTriangle,
  invoice_issued: CreditCard,
  overdue: CreditCard,
  subscription_ending: CalendarX,
  deleting: Trash2,
} as const;

/**
 * Lifecycle notices across the top of the shell (M11 §6): what is about to happen, by when, and the one
 * thing to do about it. Everything here is a warning or a countdown — a suspension is an interstitial, not
 * a banner, because at that point reads are refused too.
 *
 * The wording holds one promise throughout: terminals keep letting people through whatever the billing
 * state is (runbooks/billing.md). It is the portal and the API that stop, and only after the grace period.
 */
export function TenantStateBanner({ state, tz, canManage = false, now }: TenantStateBannerProps) {
  const danger = state.kind === "deleting" || state.kind === "overdue" || state.kind === "trial_expired";
  const linkTone = danger ? "text-danger-fg" : "text-warning-fg";
  return (
    <div
      role={danger ? "alert" : "status"}
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2 text-cell md:px-6",
        danger
          ? "border-danger/40 bg-danger-soft text-danger-fg"
          : "border-warning/50 bg-warning-soft text-warning-fg",
      )}
    >
      <Icon icon={ICON[state.kind]} size={16} />
      {state.kind === "trial_ending" ? (
        <>
          <p className="min-w-0 flex-1">
            Your trial ends {daysUntilLabel(state.endsAt, now)} ({formatOrgDate(state.endsAt, tz)}). Buy what
            you are using to keep it.
          </p>
          {canManage ? (
            <Button asChild variant="secondary" size="sm">
              <Link to={ROUTES.settingsTab("subscription")}>Subscribe</Link>
            </Button>
          ) : null}
        </>
      ) : state.kind === "addon_trial_ending" ? (
        <p className="min-w-0 flex-1">
          Your {state.itemName} trial ends {daysUntilLabel(state.endsAt, now)} (
          {formatOrgDate(state.endsAt, tz)}); it switches off then, and nothing is deleted.{" "}
          {canManage ? (
            <Link to={ROUTES.settingsTab("subscription")} className={cn("font-medium", linkTone)}>
              Keep it
            </Link>
          ) : null}
        </p>
      ) : state.kind === "trial_expired" ? (
        <>
          <p className="min-w-0 flex-1">
            Your trial ended on {formatOrgDate(state.endedAt, tz)}; reading still works, changes are paused.
          </p>
          {canManage ? (
            <Button asChild variant="secondary" size="sm" className="border-danger/40">
              <Link to={ROUTES.settingsTab("subscription")}>Subscribe</Link>
            </Button>
          ) : null}
        </>
      ) : state.kind === "invoice_issued" ? (
        <>
          <p className="min-w-0 flex-1">
            {formatMoney(state.amountPaise)} is due
            {state.dueAt ? ` on ${formatOrgDate(state.dueAt, tz)}` : ""} to renew this organisation.
          </p>
          {canManage ? (
            <Button variant="secondary" size="sm" onClick={state.onPay}>
              Pay now
            </Button>
          ) : null}
        </>
      ) : state.kind === "subscription_ending" ? (
        <>
          <p className="min-w-0 flex-1">
            Your subscription ends {daysUntilLabel(state.endsAt, now)} ({formatOrgDate(state.endsAt, tz)}).
            After that the portal locks; your data is kept and your terminals keep letting people through.
          </p>
          {canManage ? (
            <Button variant="secondary" size="sm" loading={state.resuming ?? false} onClick={state.onResume}>
              Resume
            </Button>
          ) : null}
        </>
      ) : state.kind === "overdue" ? (
        <>
          <p className="min-w-0 flex-1">
            {formatMoney(state.amountPaise)} is overdue.{" "}
            {state.graceUntil
              ? `This organisation keeps working until ${formatOrgDate(state.graceUntil, tz)} (${daysUntilLabel(state.graceUntil, now)}), then pauses.`
              : "This organisation pauses when the grace period ends."}{" "}
            Your terminals keep letting people through either way.
          </p>
          {canManage ? (
            <Button variant="secondary" size="sm" className="border-danger/40" onClick={state.onPay}>
              Pay now
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <p className="min-w-0 flex-1">
            Scheduled for deletion on {formatOrgDate(state.scheduledAt, tz)}.{" "}
            {state.canCancel ? (
              "Everything is kept until then."
            ) : (
              <>
                An owner can cancel it under{" "}
                <Link to={ROUTES.settingsTab("danger")} className="font-medium text-danger-fg">
                  Settings → Danger zone
                </Link>
                .
              </>
            )}
          </p>
          {state.canCancel ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={state.onCancel}
              loading={state.cancelling ?? false}
              className="border-danger/40"
            >
              Cancel deletion
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
